"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, ShieldCheck, Cpu, Layers } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
const keySpecs = [{
  icon: Cpu,
  title: "Linear Variable Filter (LVF)",
  description: "Solid-state optical engine without moving parts for stability."
}, {
  icon: ShieldCheck,
  title: "IP65 & IP67 Rated",
  description: "Ruggedized enclosures designed for cleanrooms and plant floors."
}, {
  icon: Layers,
  title: "Real-Time PAT Integration",
  description: "Direct flange and sanitary mounting for continuous blend monitoring."
}];
const quickModels = [{
  name: "MicroNIR PAT-W",
  path: "/MicroNirPatwPage",
  tag: "Wireless / Rotating Blenders"
}, {
  name: "MicroNIR PAT-U",
  path: "/MicroNirPatuPage",
  tag: "Fixed Installation / Pipe Flow"
}, {
  name: "MicroNIR OnSite-W",
  path: "/MicroNirOnsiteWPage",
  tag: "Handheld / Field Analysis"
}];
export default function MicronirHero() {
  return <section id="micronirhero" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border overflow-hidden">
      {/* Decorative background grid subtle overlay */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-secondary/60 via-background to-background" />

      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Text Content Column */}
          <div className="lg:col-span-7 flex flex-col items-start gap-6">
            <div className="inline-flex items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 font-medium text-xs">
                Process Analytical Technology
              </Badge>
              <span className="text-xs text-muted-foreground font-medium">
                VIAVI Solutions Series
              </span>
            </div>

            <div className="space-y-4">
              <h1 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.15]">
                VIAVI MicroNIR Spectrometer Family
              </h1>
              <p className="font-default text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
                Ultra-compact near-infrared spectrometers engineered for real-time raw material identification, blend uniformity monitoring, and continuous manufacturing processes in regulated environments.
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button asChild size="lg" className="h-11 px-6 font-medium">
                <Link to="/DiscussYourApplicationPage" className="inline-flex items-center gap-2">
                  <span>Discuss Your Application</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-11 px-6 font-medium">
                <Link to="/BookADemoPage">
                  Book a Demo
                </Link>
              </Button>
            </div>

            {/* Quick Model Links */}
            <div className="w-full pt-4 border-t border-border mt-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                Featured Product Lines
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 w-full">
                {quickModels.map((model, i) => <Link key={model.name} data-index={i} to={model.path} className="group block p-3 rounded-md border border-border bg-card/60 hover:bg-secondary/70 hover:border-border transition-colors text-left">
                    <div className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors flex items-center justify-between">
                      <span>{model.name}</span>
                      <ArrowRight className="size-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {model.tag}
                    </div>
                  </Link>)}
              </div>
            </div>
          </div>

          {/* Media & Specification Column */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            <div className="relative rounded-lg border border-border bg-card shadow-sm overflow-hidden">
              <AspectRatio ratio={16 / 10} className="bg-secondary/40">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_family_spectrometer.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_family_spectrometer.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_family_spectrometer.png-wvc-sizes" alt="VIAVI MicroNIR spectrometer product line for laboratory and process analytical applications" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </AspectRatio>
              <div className="p-4 bg-card border-t border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Certified Support Partner</p>
                    <p className="text-sm font-semibold text-foreground">Antech Solutions (Ireland & UK)</p>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs font-medium text-foreground bg-secondary px-2.5 py-1 rounded-md border border-border">
                    <CheckCircle2 className="size-3.5 text-primary" />
                    <span>21 CFR Part 11</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Highlights Grid */}
            <div className="grid grid-cols-1 gap-2.5">
              {keySpecs.map((spec, i) => {
              const Icon = spec.icon;
              return <div key={spec.title} data-index={i} className="flex items-start gap-3 p-3 rounded-md bg-secondary/30 border border-border/80">
                    <div className="p-2 rounded-md bg-secondary text-primary shrink-0 mt-0.5">
                      <Icon className="size-4" />
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-sm font-semibold text-foreground leading-snug">
                        {spec.title}
                      </h2>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {spec.description}
                      </p>
                    </div>
                  </div>;
            })}
            </div>

          </div>

        </div>
      </div>
    </section>;
}